import {Button, CheckboxTree, CheckboxTreeOptionType, Label, Search} from 'platform/components';
import {Box, Heading, Hide, Show, Stack, VStack} from 'platform/foundation';

import {FieldValues, useController} from 'react-hook-form';

import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {FeatureKey, VehicleTypeEnumObject} from '@dms/api/shared';
import {i18n} from '@dms/i18n';
import {groupFeaturesBySubCategory, useVehicleFeaturesV2} from '@dms/shared';

import {EMPTY_PLACEHOLDER, suffixTestId} from 'shared/web';

import {FeaturesPickerProps} from '../types/FeaturesPickerProps';
import {getTopMostItem, getAllChildKeys} from '../utils/checkboxTreeHelpers';
import {getFeaturesPickerRowOptions} from '../utils/getFeaturesPickerRowOptions';
import {FeatureSection, getOrderedFeatureSections} from '../utils/getOrderedFeatureSections';

const featureKeyToCheckboxTreeOption = (feature: FeatureKey): CheckboxTreeOptionType => ({
  label: feature.labels[0]?.label ?? feature.const_key,
  value: feature.const_key,
  options: feature.children.map(featureKeyToCheckboxTreeOption),
});

/**
 * Same selection behaviour as `FeaturesPickerFlat` (one selection per root tree, flat `string[]` value),
 * but the features are laid out by main category and catalogue subcategory.
 */
export function FeaturesPickerWithSubCategories<TFieldValues extends FieldValues = FieldValues>(
  props: FeaturesPickerProps<TFieldValues>
) {
  const {categorizedFeaturesTree, subCategories, search, setSearch} = useVehicleFeaturesV2(
    Object.values(VehicleTypeEnumObject).find((type) => type === props.vehicleType)
  );

  const {field} = useController({control: props.control, name: props.name});

  const isDisabled = field.disabled || props.isDisabled || false;

  const sections = getOrderedFeatureSections(categorizedFeaturesTree);
  const rootOptions = sections.flatMap((section) =>
    section.features.map(featureKeyToCheckboxTreeOption)
  );

  const handleCheckboxTreeSelect = (value: string) => {
    const selectedValues = field.value;

    if (selectedValues?.includes(value)) {
      // value is already selected, just deselect it
      field.onChange(selectedValues.filter((selectedValue: string) => selectedValue !== value));
      return;
    }
    const topMostItem = getTopMostItem(value, rootOptions);
    if (topMostItem) {
      // deselect all child keys of this parent
      const treeKeys = getAllChildKeys(topMostItem);
      const newSelection = selectedValues.filter((item: string) => !treeKeys.includes(item));
      field.onChange([...newSelection, value]);
      return;
    }
    // just add item to collection
    field.onChange([...selectedValues, value]);
  };

  const onSearchChange = (value: string | null) => {
    if (!props.isFeaturesOpen) {
      props.toggleFeaturesOpen();
    }
    setSearch(value ?? undefined);
  };

  const renderGroupTrees = (groupKey: string, features: FeatureSection['features']) => {
    const [firstOptionsRow, secondOptionsRow] = getFeaturesPickerRowOptions(
      features.map(featureKeyToCheckboxTreeOption)
    );

    return (
      <Stack width="100%" direction={['column', 'column', 'row', 'row']} spacing={[2, 2, 4, 4]}>
        <Show when={isNotNilOrEmpty(firstOptionsRow)}>
          <Box flex={1}>
            <CheckboxTree
              data-testid={suffixTestId(`featuresPicker-${groupKey}-firstColumn`, props)}
              key={search}
              value={field.value ?? null}
              onSingleValueChange={handleCheckboxTreeSelect}
              options={firstOptionsRow}
              isDisabled={isDisabled}
            />
          </Box>
        </Show>
        <Show when={isNotNilOrEmpty(secondOptionsRow)}>
          <Box flex={1}>
            <CheckboxTree
              data-testid={suffixTestId(`featuresPicker-${groupKey}-secondColumn`, props)}
              key={search}
              value={field.value ?? null}
              onSingleValueChange={handleCheckboxTreeSelect}
              options={secondOptionsRow}
              isDisabled={isDisabled}
            />
          </Box>
        </Show>
      </Stack>
    );
  };

  const renderSection = (section: FeatureSection) => {
    const groups = groupFeaturesBySubCategory({
      items: section.features,
      categoryKey: section.key,
      subCategories,
      getSubCategory: (feature) => feature.sub_category,
    });
    const hasHeadings = groups.some((group) => !group.isOther);

    return (
      <VStack
        key={section.key}
        spacing={3}
        width="100%"
        data-testid={suffixTestId(`featuresPicker-section-${section.key}`, props)}
      >
        <Heading size={4}>{section.category?.labels?.[0]?.label ?? EMPTY_PLACEHOLDER}</Heading>
        {groups.map((group) => (
          <VStack
            key={group.key}
            spacing={2}
            width="100%"
            data-testid={suffixTestId(`featuresPicker-subCategory-${group.key}`, props)}
          >
            <Show when={hasHeadings}>
              <Heading size={6} color="secondary">
                {group.label ?? i18n.t('entity.vehicle.labels.otherLabel')}
              </Heading>
            </Show>
            {renderGroupTrees(group.key, group.items)}
          </VStack>
        ))}
      </VStack>
    );
  };

  return (
    <VStack spacing={3} width="100%" align="flex-start">
      <Box width="100%">
        <Hide when={props.isLabelHidden}>
          <Label>{i18n.t('entity.vehicle.labels.features')}</Label>
        </Hide>
        <Search
          data-testid={suffixTestId('featuresPicker', props)}
          value={search ?? null}
          onChange={onSearchChange}
          isDisabled={isDisabled}
        />
      </Box>
      <Button
        data-testid={suffixTestId('featuresPicker-showOrHide', props)}
        title={
          props.isFeaturesOpen
            ? i18n.t('entity.vehicle.actions.hideAllFeatures')
            : i18n.t('entity.vehicle.actions.showAllFeatures')
        }
        variant="link"
        rightIcon={props.isFeaturesOpen ? 'navigation/expand_less' : 'navigation/expand_more'}
        onClick={props.toggleFeaturesOpen}
        isDisabled={isNilOrEmpty(sections) || isDisabled}
      />
      <Show when={props.isFeaturesOpen && isNotNilOrEmpty(sections)}>
        <VStack spacing={4} width="100%">
          {sections.map(renderSection)}
        </VStack>
      </Show>
    </VStack>
  );
}
